import React from 'react'
import Select, { components, InputProps, OptionProps, GroupBase } from 'react-select'
import { useInfiniteScroll, UseInfiniteScrollOptions } from './hooks/useInfiniteScroll'

export interface SelectOption {
  value: string
  label: string
}

interface InfiniteSelectDropdownProps<T> {
  fetchData: UseInfiniteScrollOptions<T>['fetchData']
  formatOption: (item: T) => SelectOption
  value?: SelectOption[]
  onChange: (selectedOptions: SelectOption[]) => void
  placeholder?: string
  isMulti?: boolean
  className?: string
  isDisabled?: boolean
  limit?: number
}

const CustomInput = (props: InputProps<SelectOption, boolean, GroupBase<SelectOption>>) => {
  return <components.Input {...props} isHidden={false} />
}

const CustomMenuList = (props: any) => {
  const { selectProps, innerRef, innerProps } = props
  const { loadMore, hasMore, loading, itemsCount } = selectProps as any

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget
    const { scrollTop, scrollHeight, clientHeight } = target
    const isNearBottom = scrollTop + clientHeight >= scrollHeight - 20

    console.log('Scroll event:', { 
      scrollTop, 
      scrollHeight, 
      clientHeight, 
      isNearBottom, 
      hasMore, 
      loading,
      itemsCount 
    })

    // Only trigger loadMore if we're near bottom, have more data, not loading, and have items
    if (isNearBottom && hasMore && !loading && itemsCount >= 5) {
      console.log('Triggering loadMore...')
      loadMore()
    }
  }

  return (
    <components.MenuList
      {...props}
      innerRef={innerRef}
      innerProps={{
        ...innerProps,
        onScroll: handleScroll,
      }}
    >
      {props.children}
      {loading && (
        <div style={{ 
          padding: '12px 16px', 
          textAlign: 'center', 
          color: '#6c757d',
          backgroundColor: '#f8f9fa',
          borderTop: '1px solid #e9ecef',
          fontSize: '14px',
          fontStyle: 'italic'
        }}>
          Loading more...
        </div>
      )}
      {!hasMore && itemsCount > 0 && (
        <div style={{ 
          padding: '12px 16px', 
          textAlign: 'center', 
          color: '#6c757d',
          backgroundColor: '#f8f9fa',
          borderTop: '1px solid #e9ecef',
          fontSize: '12px',
          fontStyle: 'italic'
        }}>
          No more items to load
        </div>
      )}
    </components.MenuList>
  )
}

const CustomOption = (props: OptionProps<SelectOption, boolean, GroupBase<SelectOption>>) => {
  return <components.Option {...props} />
}

function InfiniteSelectDropdown<T>({
  fetchData,
  formatOption,
  value = [],
  onChange,
  placeholder = 'Select options',
  isMulti = true,
  className,
  isDisabled = false,
  limit = 10,
}: InfiniteSelectDropdownProps<T>) {
  const {
    items,
    loading,
    hasMore,
    searchText,
    setSearchText,
    loadMore,
    setMenuIsOpen,
  } = useInfiniteScroll({
    fetchData,
    limit,
  })

  const options = items.map(formatOption)

  const handleInputChange = (inputValue: string, actionMeta: any) => {
    if (actionMeta.action === 'input-change') {
      setSearchText(inputValue)
    }
  }

  const handleMenuOpen = () => {
    setMenuIsOpen(true)
  }

  const handleMenuClose = () => {
    setMenuIsOpen(false)
  }

  const customComponents = {
    Input: CustomInput,
    MenuList: CustomMenuList,
    Option: CustomOption,
  }

  return (
    <Select<SelectOption, boolean, GroupBase<SelectOption>>
      isMulti={isMulti}
      options={options}
      value={value}
      onChange={(selectedOptions) => onChange(selectedOptions as SelectOption[])}
      placeholder={placeholder}
      className={className}
      isDisabled={isDisabled}
      closeMenuOnSelect={!isMulti}
      hideSelectedOptions={false}
      classNamePrefix="react-select"
      components={customComponents}
      onInputChange={handleInputChange}
      onMenuOpen={handleMenuOpen}
      onMenuClose={handleMenuClose}
      inputValue={searchText}
      filterOption={() => true} // We handle filtering on the server side
      isLoading={loading}
      loadingMessage={() => 'Searching...'}
      noOptionsMessage={() => 'No options found'}
      // Pass additional props to custom components
      {...({ loadMore, hasMore, loading, itemsCount: items.length } as any)}
      menuPortalTarget={document.body}
      styles={{
        // Must beat .modal's z-index (10105 — see _header.scss) or a
        // portaled menu opened inside any modal renders invisibly behind it.
        menuPortal: (base) => ({
          ...base,
          zIndex: 10110,
        }),
        menu: (base) => ({
          ...base,
          zIndex: 10110,
          backgroundColor: '#ffffff',
          border: '1px solid #e0e6ed',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          overflow: 'hidden',
        }),
        menuList: (base) => ({
          ...base,
          padding: '0',
          maxHeight: '200px',
          overflowY: 'auto',
          backgroundColor: '#ffffff',
        }),
        option: (base, state) => ({
          ...base,
          backgroundColor: state.isFocused ? '#f8f9fa' : state.isSelected ? '#007bff' : '#ffffff',
          color: state.isSelected ? '#ffffff' : '#495057',
          padding: '12px 16px',
          cursor: 'pointer',
          fontSize: '14px',
          borderBottom: '1px solid #f1f3f4',
          '&:hover': {
            backgroundColor: state.isSelected ? '#007bff' : '#f8f9fa',
          },
          '&:last-child': {
            borderBottom: 'none',
          },
        }),
        control: (base, state) => ({
          ...base,
          border: state.isFocused ? '2px solid #007bff' : '1px solid #e0e6ed',
          borderRadius: '6px',
          boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(0, 123, 255, 0.25)' : 'none',
          '&:hover': {
            border: state.isFocused ? '2px solid #007bff' : '1px solid #ced4da',
          },
        }),
        placeholder: (base) => ({
          ...base,
          color: '#6c757d',
          fontSize: '14px',
        }),
        singleValue: (base) => ({
          ...base,
          color: '#495057',
          fontSize: '14px',
        }),
        multiValue: (base) => ({
          ...base,
          backgroundColor: '#e9ecef',
          borderRadius: '4px',
        }),
        multiValueLabel: (base) => ({
          ...base,
          color: '#495057',
          fontSize: '12px',
        }),
        multiValueRemove: (base) => ({
          ...base,
          color: '#6c757d',
          '&:hover': {
            backgroundColor: '#dc3545',
            color: '#ffffff',
          },
        }),
        input: (base) => ({
          ...base,
          color: '#495057',
          fontSize: '14px',
        }),
        loadingIndicator: (base) => ({
          ...base,
          color: '#007bff',
        }),
        dropdownIndicator: (base, state) => ({
          ...base,
          color: state.isFocused ? '#007bff' : '#6c757d',
          '&:hover': {
            color: '#007bff',
          },
        }),
        clearIndicator: (base) => ({
          ...base,
          color: '#6c757d',
          '&:hover': {
            color: '#dc3545',
          },
        }),
      }}
    />
  )
}

export default InfiniteSelectDropdown